import style from './style/ColumnsRepeater.scss'
import type { ComponentType, MouseEventHandler } from 'react'
import React, { createRef, useCallback, useMemo } from 'react'
import semanticClassNames from '@wix/editor-elements-semantic-classnames/dist/libs/semanticClassNames.json'
import { formatClassNames } from '@wix/editor-elements-common-utils'
import type { ColumnsRepeaterCompProps } from './types'
import { ColumnsRepeaterController } from './ColumnsRepeater.controller'
import { splitArrayIntoRows } from './utils'

const repeaterSemanticClassName = semanticClassNames.Repeater.Repeater.root || ''

const ColumnsRepeater: ComponentType<ColumnsRepeaterCompProps> = ({
	id,
	children,
	items,
	columns: columnsBasedOnWidth,
	onMouseEnter,
	onMouseLeave,
	a11y = {},
	rowRole,
	cellRole,
	className,
	customClassNames = [],
	observeChildListChange,
	shouldRenderFlexRows,
}) => {
	const { label, role } = a11y
	const columns = useMemo(() => Math.min(columnsBasedOnWidth ?? 1, items.length), [columnsBasedOnWidth, items.length])
	const numberOfRows = useMemo(() => Math.ceil(items.length / columns), [columns, items.length])

	const itemsInRows = useMemo(() => splitArrayIntoRows(numberOfRows, items), [items, numberOfRows])

	const itemsInColumn = items.reduce<Array<Array<string>>>(
		(acc, item, index) => {
			const column = index % columns
			acc[column].push(item)
			return acc
		},
		new Array(columns).fill(null).map(() => [])
	)

	const cellRefsRowFlex = useMemo(() => items.map(() => createRef<HTMLDivElement>()), [items])
	const cellRefs = useMemo(
		() => itemsInColumn.map((column) => column.map(() => createRef<HTMLDivElement>())),
		[itemsInColumn]
	)

	const listWrapperRef = useCallback((listWrapper: HTMLElement | null) => {
		if (observeChildListChange && listWrapper) {
			observeChildListChange(id, listWrapper, true)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const renderFlexColumns = useMemo(
		() =>
			itemsInColumn.map((column, columnIndex) => (
				<div ref={listWrapperRef} role={rowRole} className={style.rowOld} key={columnIndex}>
					{column.map((childId, rowIndex) => (
						<div role={cellRole} className={`${style.listItemOld}`} key={childId} ref={cellRefs[columnIndex][rowIndex]}>
							{children({
								parentType: 'Repeater',
								scopeId: childId,
								itemIndex: rowIndex * itemsInColumn.length + columnIndex,
							})}
						</div>
					))}
				</div>
			)),
		[cellRefs, cellRole, children, itemsInColumn, listWrapperRef, rowRole]
	)

	const renderFlexRows = useMemo(
		() =>
			itemsInRows.map((row, rowIndex) => {
				return (
					<div ref={listWrapperRef} className={style.row} key={rowIndex}>
						{row.map((cellId, index) => {
							return (
								<div role={cellRole} className={`${style.listItem}`} key={cellId} ref={cellRefsRowFlex[index]}>
									{children({
										parentType: 'Repeater',
										scopeId: cellId,
										itemIndex: index,
									})}
								</div>
							)
						})}
					</div>
				)
			}),
		[cellRefsRowFlex, cellRole, children, itemsInRows, listWrapperRef]
	)

	return (
		<div
			id={id}
			onMouseEnter={onMouseEnter as MouseEventHandler}
			onMouseLeave={onMouseLeave as MouseEventHandler}
			className={[style.wrapper, className, formatClassNames(repeaterSemanticClassName, ...customClassNames)].join(' ')}
		>
			<div role={role} aria-label={label} className={shouldRenderFlexRows ? style.list : style.listOld}>
				{shouldRenderFlexRows ? renderFlexRows : renderFlexColumns}
			</div>
		</div>
	)
}

export const component = ColumnsRepeater

export const controller = ColumnsRepeaterController
